import type { InputTableQuerySpec } from "@selector/shared/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CalloutIntent, DialogComponent, Modal, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import { createSelectionModel, type ISelectionModel } from "@/app-utils/selection-model.ts";
import { createUniqueKeyFromTableSpec, getTableDataSlice } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { VariantMutantGenomesSelectionActionButton } from "@/ui-modules/genome-viewer/VariantMutantGenomesSelectionForm.tsx";

interface CoordinatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultValue: {
    genomeId: number | null;
    coordinateListId: number | null;
    selectedIds: number[];
    restrictionsIds: number[];
    rowFilter: string | undefined;
  };
  onSaveSelection: (coordinates: number[], variantGenomes: number[], rowFilter?: string) => Promise<void>;
}

export const CoordinatesModal = ({
  isOpen,
  onClose,
  defaultValue: { genomeId, coordinateListId, selectedIds, restrictionsIds, rowFilter },
  onSaveSelection,
}: CoordinatesModalProps) => {
  const { overlayProps, state } = useOverlay({ isOpen });
  const [selection, setSelection] = useState<ISelectionModel["selectedIds"]>(selectedIds);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variantGenomeIds, setVariantGenomeIds] = useState(restrictionsIds);

  const tableSpec: InputTableQuerySpec | null = useMemo(
    () =>
      coordinateListId && genomeId
        ? {
            id: coordinateListId,
            idType: DATATABLE_ID_TYPE.ENTITY,
            queryType: DATATABLE_PARENT_TYPE.COORD_LIST,
            resultType: ENTITY_TYPE.COORDINATE,
            restrictions: { refGenomeId: genomeId, variantGenomeIds },
            rowFilter,
            context: "tableParam", // to distinguish the coordinates table in the widget from the coordinates table visible e.g. in the genome viewer, from where the Playbook was launched
          }
        : null,
    [coordinateListId, genomeId, variantGenomeIds, rowFilter],
  );

  const tableRowFilter = useSelector<IAppState, string | undefined>((state) => {
    if (!tableSpec) {
      return undefined;
    }

    return getTableDataSlice(state.tableData, tableSpec)?.filterExpression;
  });

  const handleSelectionChange = useCallback(() => {
    const filteredCoordinates = selection.map(Number).filter((l) => !Number.isNaN(l));

    if (coordinateListId == null) {
      onClose();
      return;
    }

    setIsSaving(true);
    onSaveSelection(filteredCoordinates, variantGenomeIds, tableRowFilter)
      .then(() => {
        onClose();
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [coordinateListId, onClose, onSaveSelection, selection, tableRowFilter, variantGenomeIds]);

  useEffect(() => {
    if (isOpen) {
      setSelection(selectedIds);
      setVariantGenomeIds(restrictionsIds);
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen, restrictionsIds, selectedIds]);

  if (!tableSpec) {
    return null;
  }

  const tableUniqueKey = createUniqueKeyFromTableSpec(tableSpec);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        fullscreen
        title="Select Coordinates"
        mainButton={{
          label: `Select ${selection.length === 0 ? "All" : "(" + selection.length + ")"}`,
          onPress: handleSelectionChange,
          isDisabled: false,
          isLoading: isSaving,
        }}
        actions={[<ColumnSelectionButton key="column-selection-button-placeholder" portalContext={tableUniqueKey} />]}
        closeFn={() => {
          setIsSaving(false);
          setError(null);
          onClose();
        }}
        errorCallout={error ? { intent: CalloutIntent.Danger, message: error } : undefined}
        contentStyle={{ display: "grid", padding: 0, borderTop: "1px solid var(--ui-border)" }}
        infoComponent={
          <VariantMutantGenomesSelectionActionButton
            onSave={setVariantGenomeIds}
            genomeId={tableSpec.restrictions!.refGenomeId!}
            restrictionVariantGenomesIds={variantGenomeIds}
          />
        }
      >
        <VStack style={{ width: "100%", height: "100%" }}>
          {/* @ts-expect-error Reported missing properties of this component are actually not required */}
          <DataTableContainer
            key={tableUniqueKey}
            tableSpec={tableSpec}
            gridContext={{ tableSpec }}
            noRowsText="No coordinates found"
            onSelectionModelChanged={(selected) => {
              setError(null);
              setSelection(selected.selectedIds);
            }}
            tableInitializationOptions={{
              initialSelectionModel: createSelectionModel(selectedIds),
              filterExpression: tableSpec.rowFilter ?? undefined,
            }}
          />
        </VStack>
      </DialogComponent>
    </Modal>
  );
};
